import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { logger } from 'shared/lib'
import type {
  IAudioFileSetting,
  IParamsUpdateUserSettings,
  IParamsUpdateUserSettingsMultiple,
  IResponseUpdateUserSettingsMultiple,
  ISetting,
} from 'entities/Settings/api/types'
import { SettingsApi } from 'entities/Settings/api/settings'
import { setVersionDesign } from 'entities/Settings/lib/setVersionDesign'
import { type FeatureFlagsStore } from 'entities/FeatureFlags'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseSetOptionsNotifications } from 'entities/Users/api/types.ts'

export class UserSettingsStore {
  initPromise
  private _setFinishInit: ((value?: unknown) => void) | null = null
  private _settingsMap: Map<ISetting['key'], ISetting> = new Map()
  private _settingsLoading = false
  private readonly _disposeIsDesignV2: IReactionDisposer | null = null
  private _featureFlagsStore: FeatureFlagsStore | null = null

  constructor() {
    makeAutoObservable(this)

    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })

    this._disposeIsDesignV2?.()
    this._disposeIsDesignV2 = reaction(
      () => this.isSalesmsgDesignV2,
      (value) => {
        setVersionDesign(value)
      }
    )
  }

  setEntities = (entities: IEntitiesStore) => {
    this._featureFlagsStore = entities.featureFlagsStore
  }

  fetchUsersSettings = async () => {
    try {
      runInAction(() => {
        this._settingsLoading = true
      })

      const { data } = await SettingsApi.getUsersSettings()

      runInAction(() => {
        data.forEach((item) => {
          this._settingsMap.set(item.key, item)
        })
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._settingsLoading = false
        this._setFinishInit?.()
      })
    }
  }

  updateSetting = (params: IParamsUpdateUserSettings) => this._fetchUpdateSettings([params])

  updateSettingsMultiple = (params: IParamsUpdateUserSettingsMultiple) =>
    this._fetchUpdateSettings(params)

  private _fetchUpdateSettings = async (params: IParamsUpdateUserSettingsMultiple) => {
    try {
      runInAction(() => {
        this._settingsLoading = true
      })

      const results: IResponseUpdateUserSettingsMultiple =
        params.length === 1
          ? await SettingsApi.updateUsersSetting(params[0]).then(({ data }) => (data ? [data] : []))
          : await SettingsApi.updateUsersSettingsMultiple(params).then(({ data }) => data ?? [])

      runInAction(() => {
        results.forEach(({ featureKey, settingsKey, value }) => {
          const item = this._settingsMap.get(featureKey)

          if (item) {
            this._settingsMap.set(featureKey, {
              ...item,
              settings: {
                ...item.settings,
                [settingsKey]: value,
              },
            })
          }
        })
      })

      return true
    } catch (e) {
      logger.error(e)

      return false
    } finally {
      runInAction(() => {
        this._settingsLoading = false
      })
    }
  }

  patchSettings = (data: IResponseSetOptionsNotifications) => {
    data.forEach((dataItem) => {
      const { featureKey, settingsKey, value } = dataItem
      const item = this._settingsMap.get(featureKey)
      if (item) {
        this._settingsMap.set(featureKey, {
          ...item,
          settings: {
            ...item.settings,
            [settingsKey]: value,
          },
        })
      }
    })
  }

  getItem = (key: string) => {
    return this._settingsMap.get(key)
  }

  get isUserShowActivity() {
    return Boolean(this._settingsMap.get('conversation')?.settings.show_activity)
  }

  get isHideCalls() {
    return Boolean(this._settingsMap.get('conversation')?.settings.hide_calls)
  }

  get isSalesmsgDesignV2() {
    if (this._featureFlagsStore?.show_new_design_only) {
      return true
    }

    return this.hasSalesmsgDesignV2SettingsKeyActive
  }

  get hasSalesmsgDesignV2SettingsKeyActive() {
    return Boolean(this._settingsMap.get('salesmsg-design-v2')?.settings.active)
  }

  get isClickToUndo() {
    return Boolean(this._settingsMap.get('message-form')?.settings?.click_to_undo)
  }

  get settingVoicemailDrop() {
    const item = this._settingsMap.get('voicemail-drop')

    if (!item) return

    return item as ISetting<{
      audiofiles: IAudioFileSetting[]
    }>
  }

  get hasPublicApiClient() {
    return Boolean(this._settingsMap.get('public-api-client')?.settings.active)
  }

  get isHubspotTicketOwnerAutoAssignActive() {
    return Boolean(this._settingsMap.get('hubspot-ticket-owner-auto-assign')?.settings.active)
  }

  get hubspotTicketOwnerAssignTo() {
    const value = this._settingsMap.get('hubspot-ticket-owner-auto-assign')?.settings.assign_to
    return typeof value === 'string' ? value : 'conversation_owner'
  }
}
